import React from 'react';
import { DateTime } from 'luxon';
import { useAgencyDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/AgencyDashboard/store/agencyDashboard.store';
import {
  useChartColors,
  REPORTS_PAGE_COLORS,
} from 'pages/ReportsPage/components/AnalyticsDashboards/chartColors';
import {
  VerticalBarChart,
  VerticalBarItem,
} from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/VerticalBarChart';
import { EmptyStateContainer } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/OverviewDashboard.styled';
import { formatDashboardNumber } from 'pages/ReportsPage/components/AnalyticsDashboards/formatDashboardNumber';

function formatBucketLabel(bucketStart: string): string {
  const [datePart, weekday] = bucketStart.split(' ');
  const parsed = DateTime.fromISO(datePart, { setZone: true });
  const monthDay = parsed.isValid ? parsed.toFormat('MMM d') : datePart;
  return weekday ? `${weekday}\n${monthDay}` : monthDay;
}

export const AgencyHoursDistributionChart: React.FC = () => {
  const data = useAgencyDashboardStore((s) => s.hoursDistribution);
  const hasError = useAgencyDashboardStore((s) => s.hoursDistributionError);
  const palette = useChartColors();

  if (hasError) {
    return <EmptyStateContainer>Failed to load agency hours distribution data</EmptyStateContainer>;
  }

  if (!data || data.length === 0) {
    return (
      <EmptyStateContainer>
        No agency hours data available for the selected filters.
      </EmptyStateContainer>
    );
  }

  const barColor = palette.chartColors[0];

  const items: VerticalBarItem[] = data.map((d) => {
    const hoursLabel = `${formatDashboardNumber(d.hours)}h`;
    const rateLabel =
      d.sentToAgencyRate == null ? null : `${formatDashboardNumber(d.sentToAgencyRate)}%`;
    return {
      key: d.bucketStart,
      value: d.hours,
      barColor,
      ariaLabel: `${d.bucketStart}: ${hoursLabel} agency hours${
        rateLabel ? `, sent to agency ${rateLabel}` : ''
      }`,
      tooltipHtml: `<strong>${d.bucketStart}</strong><br/>Agency hours: ${hoursLabel}${
        rateLabel ? `<br/>Sent to agency: ${rateLabel}` : ''
      }`,
      aboveLabels: rateLabel ? [{ text: rateLabel, color: palette.indicator.neutral }] : undefined,
      subLabels: [{ text: hoursLabel, color: REPORTS_PAGE_COLORS.textPrimary }],
    };
  });

  return (
    <VerticalBarChart
      items={items}
      xTickFormat={formatBucketLabel}
      ariaLabel="Agency hours distribution chart showing agency hours and sent-to-agency rate over time"
    />
  );
};
